<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"
import { formatAddress } from "@core/helpers"
import { SOURCES_TYPES } from "@core/constants"
import { ConsentRequestService } from "@core/services"
import { BuildingService } from "@core/services"
import type { Address } from "@types"
import {
    useFetchWrapper,
    type CompanyType,
    type OwnerOutput,
} from "@core/composables"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Récapitulatif",
            subtitle: "Vérifiez les informations avant de créer la demande",
            ownerTitle: "Titulaire",
            buildingsTitle: "Bâtiments et compteurs",
            buildingsTitleWithCount:
                "{buildings} bâtiment(s) • {meters} compteur(s)",
            modify: "Modifier",
            submit: "Créer la demande",
            return: "Retour",
            ownerInfo: "Informations du titulaire",
            ownerCompanyTypeEntreprise: "Entreprise",
            ownerCompanyTypeAsso: "Association",
            ownerCompanyTypeCollectivite: "Collectivité",
            ownerCompanyTypeCopro: "Copropriété",
            ownerUser: "Représentant / Client",
            errorMessage:
                "Une erreur est survenue lors de la création de la demande",
            invalidRequests: "Certaines demandes sont invalides",
            invalidRequestsList: "Compteurs invalides :",
            rollbackInProgress:
                "Annulation en cours : suppression des bâtiments créés…",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface BuildingInput {
    name: string
    address: Address
    location: number[]
    sources: Array<{
        idtype: string
        idsource: string
        date_from_data?: string | null
    }>
}

interface Props {
    /**
     * Informations du titulaire
     */
    owner: OwnerOutput
    /**
     * Indique si la demande est pour une personne tierce
     */
    thirdParty?: boolean
    /**
     * Liste des bâtiments avec leurs sources
     */
    buildings: BuildingInput[]
}

interface Emits {
    /**
     * Émis après validation du récap : création API seulement si tiers (invitation).
     * Sinon `null` et passage à l’étape signature sans appel réseau.
     * @param invitation - Données de la demande créée (tiers) ou `null` (titulaire connecté)
     */
    (e: "done:submit", invitation: unknown | null): void
    /**
     * Émis lors du clic sur le bouton retour
     */
    (e: "click:return"): void
    /**
     * Émis lors du clic sur le bouton modifier
     * @param step - Étape à modifier ('owner' | 'buildings')
     */
    (e: "click:update", step: "owner" | "buildings"): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    thirdParty: false,
})

const emit = defineEmits<Emits>()

/**
 * Clés i18n par type d'organisation (aligné sur `CompanyType`)
 */
const OWNER_COMPANY_TYPE_LABEL_KEYS = {
    entreprise: "ownerCompanyTypeEntreprise",
    asso: "ownerCompanyTypeAsso",
    collectivite: "ownerCompanyTypeCollectivite",
    copro: "ownerCompanyTypeCopro",
} as const satisfies Record<CompanyType, string>

// ================================
// SERVICES
// ================================
const consentRequestService = new ConsentRequestService()
const buildingService = new BuildingService()

// ================================
// ÉTAT
// ================================
/**
 * État d'ouverture du collapsible titulaire
 */
const isOwnerOpen = ref(false)

/**
 * État d'ouverture du collapsible bâtiments
 */
const isBuildingsOpen = ref(false)

/**
 * Liste des demandes invalides retournées par l'API
 */
const invalidRequests = ref<Array<{ idsource: string; idtype: string }>>([])

/**
 * True pendant le rollback (suppression des bâtiments après échec création demande).
 */
const isRollingBack = ref(false)

// ================================
// SOUMISSION
// ================================
/**
 * Wrapper pour la création de la demande multi
 */
const submitFetch = useFetchWrapper(
    async () => {
        // Parcours tiers uniquement : création bâtiments + demande (invitation)
        let createdBuildingIds: string[] = []
        try {
            const createdBuildings = await buildingService.createBuildings(
                props.buildings.map((building) => ({
                    name: building.name,
                    address: building.address,
                    location: {
                        coordinates: building.location,
                    },
                }))
            )
            createdBuildingIds = createdBuildings.map((b) => b._id)

            const consentRequests = createdBuildings.flatMap(
                (building, index) => {
                    return (
                        props.buildings[index]?.sources.map((source) => ({
                            address: building.address,
                            idtype: source.idtype as "pdl" | "pce",
                            idsource: source.idsource,
                            date_from_data: source.date_from_data || null,
                            _id_building: building._id,
                        })) || []
                    )
                }
            )

            const data = await consentRequestService.createConsentRequestMulti({
                user: props.owner.user,
                company: props.owner.company || null,
                consentRequests,
            })

            if (
                data?.invalidConsentRequests &&
                Array.isArray(data.invalidConsentRequests)
            ) {
                invalidRequests.value = data.invalidConsentRequests
            } else {
                emit("done:submit", data)
            }
        } catch (e) {
            if (createdBuildingIds.length > 0) {
                isRollingBack.value = true
                try {
                    await Promise.all(
                        createdBuildingIds.map((id) =>
                            buildingService.deleteBuilding(id)
                        )
                    )
                } finally {
                    isRollingBack.value = false
                }
            }
            throw e
        }
    },
    () => {
        // L'erreur est gérée par useFetchWrapper et affichée via errorMessage
    }
)

// ================================
// COMPUTED
// ================================
/**
 * Nombre total de sources
 */
const getTotalSources = computed(() => {
    return props.buildings.reduce(
        (acc, building) => acc + building.sources.length,
        0
    )
})

/**
 * Adresse formatée de l'entreprise
 */
const getCompanyAddress = computed(() => {
    return props.owner.company?.address
        ? formatAddress(props.owner.company.address)
        : ""
})

/**
 * Libellé du type d'organisation (Entreprise, Association, Collectivité, Copropriété)
 */
const getOwnerCompanyTypeLabel = computed(() => {
    const type = props.owner.company?.type
    if (!type) return ""
    const key = OWNER_COMPANY_TYPE_LABEL_KEYS[type as CompanyType]
    return key ? t(key) : type
})

/**
 * Message d'erreur à afficher
 */
const getErrorMessage = computed(() => {
    if (submitFetch.error.value) {
        return t("errorMessage")
    }
    if (invalidRequests.value.length > 0) {
        return t("invalidRequests")
    }
    return null
})

/**
 * Boutons désactivés pendant soumission ou rollback (réessai bloqué pendant suppression).
 */
const getIsSubmitBusy = computed(
    () => submitFetch.isFetching.value || isRollingBack.value
)

// ================================
// INTERACTIONS
// ================================
/**
 * Gère le clic sur le header du collapsible titulaire
 */
function onClickOwnerHeader() {
    isOwnerOpen.value = !isOwnerOpen.value
}

/**
 * Gère le clic sur le header du collapsible bâtiments
 */
function onClickBuildingsHeader() {
    isBuildingsOpen.value = !isBuildingsOpen.value
}

/**
 * Gère le clic sur le bouton modifier du titulaire
 */
function onClickModifyOwner() {
    emit("click:update", "owner")
}

/**
 * Gère le clic sur le bouton modifier des bâtiments
 */
function onClickModifyBuildings() {
    emit("click:update", "buildings")
}

/**
 * Gère le clic sur le bouton retour
 */
function onClickReturn() {
    emit("click:return")
}

/**
 * Gère la soumission du formulaire
 */
function onSubmit() {
    invalidRequests.value = []
    if (!props.thirdParty) {
        emit("done:submit", null)
        return
    }
    submitFetch.fetch()
}
</script>

<template>
    <div class="source-form-multi-summary flex flex-col gap-6 w-full">
        <!-- ================================ -->
        <!-- TITRE -->
        <!-- ================================ -->
        <div class="flex flex-col gap-2 text-center">
            <h2 class="text-xl font-semibold text-gray-700">
                {{ t("title") }}
            </h2>
            <p class="text-sm text-gray-500">{{ t("subtitle") }}</p>
        </div>

        <!-- ================================ -->
        <!-- COLLAPSIBLE TITULAIRE -->
        <!-- ================================ -->
        <div class="flex flex-col border-b border-gray-200 pb-6">
            <div
                class="flex items-center justify-between w-full cursor-pointer py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                role="button"
                tabindex="0"
                :aria-expanded="isOwnerOpen"
                aria-controls="source-form-multi-summary-owner-panel"
                @click="onClickOwnerHeader"
                @keydown.enter.prevent="onClickOwnerHeader"
                @keydown.space.prevent="onClickOwnerHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{ t("ownerTitle") }}
                </h3>
                <div class="flex items-center gap-3">
                    <BaseButton
                        v-if="isOwnerOpen && thirdParty"
                        variant="secondary"
                        size="small"
                        text
                        :label="t('modify')"
                        @click.stop="onClickModifyOwner"
                    />
                    <BaseIcon
                        :icon="
                            isOwnerOpen
                                ? 'pi pi-chevron-up'
                                : 'pi pi-chevron-down'
                        "
                        class="text-gray-500"
                    />
                </div>
            </div>

            <div
                v-show="isOwnerOpen"
                id="source-form-multi-summary-owner-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <p class="text-sm text-gray-500">{{ t("ownerInfo") }}</p>

                <!-- Informations entreprise -->
                <div
                    v-if="owner.company"
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span class="text-xs font-medium text-gray-500">
                        {{ getOwnerCompanyTypeLabel }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.company.name }}
                        <template v-if="getCompanyAddress">
                            • {{ getCompanyAddress }}
                        </template>
                        <template
                            v-if="
                                owner.company.type !== 'collectivite' &&
                                owner.company.idcompany
                            "
                        >
                            • {{ owner.company.idcompany }}
                        </template>
                    </p>
                </div>

                <!-- Informations utilisateur -->
                <div
                    class="flex flex-col gap-1 p-3 bg-gray-50 rounded border border-gray-200"
                >
                    <span
                        v-if="owner.company"
                        class="text-xs font-medium text-gray-500"
                    >
                        {{ t("ownerUser") }}
                    </span>
                    <p class="text-sm text-gray-700">
                        {{ owner.user.firstname }} {{ owner.user.lastname }} •
                        {{ owner.user.username?.toLowerCase() }}
                    </p>
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- COLLAPSIBLE BÂTIMENTS -->
        <!-- ================================ -->
        <div class="flex flex-col pb-6">
            <div
                class="flex items-center justify-between w-full cursor-pointer py-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2"
                role="button"
                tabindex="0"
                :aria-expanded="isBuildingsOpen"
                aria-controls="source-form-multi-summary-buildings-panel"
                @click="onClickBuildingsHeader"
                @keydown.enter.prevent="onClickBuildingsHeader"
                @keydown.space.prevent="onClickBuildingsHeader"
            >
                <h3 class="text-base font-semibold text-gray-700">
                    {{
                        t("buildingsTitleWithCount", {
                            buildings: buildings.length,
                            meters: getTotalSources,
                        })
                    }}
                </h3>
                <div class="flex items-center gap-3">
                    <BaseButton
                        v-if="isBuildingsOpen"
                        variant="secondary"
                        size="small"
                        text
                        :label="t('modify')"
                        @click.stop="onClickModifyBuildings"
                    />
                    <BaseIcon
                        :icon="
                            isBuildingsOpen
                                ? 'pi pi-chevron-up'
                                : 'pi pi-chevron-down'
                        "
                        class="text-gray-500"
                    />
                </div>
            </div>

            <div
                v-show="isBuildingsOpen"
                id="source-form-multi-summary-buildings-panel"
                class="flex flex-col gap-4 pt-4"
            >
                <div
                    v-for="(building, index) in buildings"
                    :key="`building-${index}`"
                    class="p-4 border border-gray-200 rounded bg-gray-50 hover:border-cyan-400 transition-colors"
                >
                    <ItemBuildingRecap
                        :name="building.name"
                        :address="building.address"
                        :pdl="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PDL)
                                .map((s) => s.idsource)
                        "
                        :pce="
                            building.sources
                                .filter((s) => s.idtype === SOURCES_TYPES.PCE)
                                .map((s) => s.idsource)
                        "
                    />
                </div>
            </div>
        </div>

        <!-- ================================ -->
        <!-- MESSAGE D'ERREUR / INVALIDS -->
        <!-- ================================ -->
        <BaseMessage
            v-if="isRollingBack"
            severity="info"
            :closable="false"
            class="source-form-multi-summary__rollback"
        >
            <p class="font-medium">{{ t("rollbackInProgress") }}</p>
        </BaseMessage>

        <BaseMessage v-if="getErrorMessage" severity="warn" :closable="false">
            <p class="font-medium">{{ getErrorMessage }}</p>
            <div v-if="invalidRequests.length > 0" class="mt-2">
                <p class="text-sm">{{ t("invalidRequestsList") }}</p>
                <p class="text-sm mt-1">
                    {{ invalidRequests.map((req) => req.idsource).join(" • ") }}
                </p>
            </div>
        </BaseMessage>

        <!-- ================================ -->
        <!-- ACTIONS -->
        <!-- ================================ -->
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                severity="secondary"
                class="min-w-[10rem] w-full md:w-auto"
                :label="t('return')"
                :disabled="getIsSubmitBusy"
                @click="onClickReturn"
            />
            <BaseButton
                severity="primary"
                class="min-w-[10rem] w-full md:w-auto"
                :label="t('submit')"
                :loading="getIsSubmitBusy"
                :disabled="getIsSubmitBusy"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
